import { useEffect, useMemo, useRef, useState } from 'react';
import { SpinnerDefault, Icon, IconDefinitions } from '../../components';
import { Button, Badge } from 'react-bootstrap';
import CTEyeService from '../../services/CTEyeService';
import { UrlService, UserService } from '../../services';
import { Roles } from '../../models';
import { useParams, useNavigate } from 'react-router-dom';
import { useSharedState } from '../../app/sharedProperty';
import { POLL_LIVE_DETECTION_MS, ONLINE_THRESHOLD_MS } from './helper';
import { ActiveConfigurationCard, AgentLiveInspectionsBanner, ClientStatusCard, DisableClientModal, EditClientModal, SystemInformationCard, DataFilesGrid} from './components';
import { ActiveConfiguration, ClientRegistrationStatus, CTEyeClientModel, CTEyeClientResponse, CTEyeSubscription, HeartbeatItemResponse, HeartbeatPayloadDetailed, LiveInspectionResponse } from '../../data/entities';

export function CTEyeClientDetails() {
  const urlParams = useParams();
  const id = UrlService.getParamValue('id', urlParams) ?? '0';
  const navigate = useNavigate();

  const [data, setData] = useState<CTEyeClientModel | null>(null);
  const [latestHeartbeat, setLatestHeartbeat] = useState<HeartbeatItemResponse | null>(null);
  const [subscription, setSubscription] = useState<CTEyeSubscription | null>(null);
  const [dataFilesKey, setDataFilesKey] = useState(0);
  const [liveInspections, setLiveInspections] = useState<LiveInspectionResponse[]>([]);

  const isAdmin = UserService.isInRole(Roles.admin);
  const canViewConfiguration = isAdmin || UserService.isInRole(Roles.staff) || UserService.isInRole(Roles.dataAnalyst);

  const prevLiveFilePathsRef = useRef<string>('');

  const showEditModalState = useSharedState(false);
  const showDisableModalState = useSharedState(false);

  const ctEyeService = useMemo(() => new CTEyeService(), []);

  // Initial load (client data, heartbeat, and subscription)
  useEffect(() => {
    const loadInitialData = async () => {
      const service = new CTEyeService();
      const [clientData, heartbeat, sub] = await Promise.allSettled([
        service.getClientByIdOData(id),
        service.getLatestHeartbeat(id),
        service.getActiveSubscription(id),
      ]);

      if (clientData.status === 'fulfilled') setData(clientData.value);
      if (heartbeat.status === 'fulfilled') setLatestHeartbeat(heartbeat.value);
      if (sub.status === 'fulfilled') setSubscription(sub.value);
    };

    loadInitialData();
  }, [id]);

  // Check if there are any live inspections happening
  useEffect(() => {
    setLiveInspections([]);

    const load = async () => {
      try {
        const inspections = await ctEyeService.getLiveInspectionsByClient(id);
        setLiveInspections(inspections);

        // Check if live files changed to update data files grid
        const currentPaths = inspections.map(i => i.filePath).sort().join(',');
        if (currentPaths !== prevLiveFilePathsRef.current) {
          prevLiveFilePathsRef.current = currentPaths;
          setDataFilesKey(k => k + 1);
        }
      } catch (err) {
        console.error('Error polling live inspections:', err);
      }
    };

    load();
    const interval = setInterval(load, POLL_LIVE_DETECTION_MS);
    return () => clearInterval(interval);
  }, [id]);

  // Poll heartbeat for online status + system info 
  useEffect(() => {
    const poll = async () => {
      try {
        const heartbeat = await ctEyeService.getLatestHeartbeat(id);
        setLatestHeartbeat(heartbeat);
      } catch (err) {
        console.error('Error polling heartbeat:', err);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_LIVE_DETECTION_MS);
    return () => clearInterval(interval);
  }, [id]);


  const handleEditClick = () => showEditModalState.setter(true);
  const handleDisableClick = () => showDisableModalState.setter(true);

  const handleModalSuccess = async () => {
    const service = new CTEyeService();
    const [clientData, sub] = await Promise.allSettled([
      service.getClientByIdOData(id),
      service.getActiveSubscription(id),
    ]);
    if (clientData.status === 'fulfilled') setData(clientData.value);
    if (sub.status === 'fulfilled') setSubscription(sub.value);
  };

  const handleWatchLive = (inspection: LiveInspectionResponse) => {
    const params = new URLSearchParams({ filePath: inspection.filePath });
    navigate(`/cteye/clients/${id}/live-view?${params.toString()}`);
  };

  const clientForModal: CTEyeClientResponse | null = useMemo(() => data
    ? {
        id,
        description: data.description,
        companyId: data.companyId,
        companyName: data.company?.name ?? null,
        runningVersion: data.runningVersion,
        registrationStatus: data.registrationStatus,
        createdDate: data.createdDate,
        registeredDate: data.registeredDate,
        lastSeenDate: data.lastSeenDate,
        isHeartbeatLoggingOn: data.isHeartbeatLoggingOn,
        monthlyInspectionLimit: subscription?.monthlyInspectionLimit,
        monthlyReportLimit: subscription?.monthlyReportLimit,
        gaugeSettings: data.gaugeSettings,
      }
    : null, [data, id, subscription]);

  if (data === null) return <SpinnerDefault />;

  const isDisabled = data.registrationStatus === ClientRegistrationStatus.Disabled;
  const isOnline = !isDisabled && latestHeartbeat?.dateTime && new Date(latestHeartbeat.dateTime) > new Date(Date.now() - ONLINE_THRESHOLD_MS);

  const heartbeatPayload = latestHeartbeat?.payload as unknown as HeartbeatPayloadDetailed | undefined;
  const hostInfo = heartbeatPayload?.hostInfo;
  const activeConfiguration = heartbeatPayload?.activeConfiguration as ActiveConfiguration | undefined;

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-10">
          {/* Header Card */}
          <div className="card shadow-sm border-0 mb-4" style={{ overflow: 'hidden' }}>
            {/*Live Inspection Banner */}
            <AgentLiveInspectionsBanner
              inspections={liveInspections}
              onWatchLive={handleWatchLive}
            />
            {/* Client Info */}
            <div className="card-body p-3 p-sm-4">
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3">
                <div
                  className={`rounded-circle d-none d-sm-flex align-items-center justify-content-center bg-${isDisabled ? 'danger' : 'primary'} bg-opacity-10 flex-shrink-0`}
                  style={{ width: 55, height: 55, gap: 3 }}
                >
                  <span className={`fw-semibold text-${isDisabled ? 'danger' : 'primary'}`}>CT</span>
                  <Icon 
                    iconName={IconDefinitions.CTEye} 
                    className={`text-${isDisabled ? 'danger' : 'primary'}`} 
                    style={{ fontSize: '0.8rem' }} 
                  />
                </div>
                <div className="min-w-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h5 className="mb-0 fw-semibold text-break">{data.description}</h5>
                    <Badge
                      pill
                      bg={isDisabled ? 'danger' : 'success'}
                      className="bg-opacity-10 fs6"
                      style={{ fontSize: '0.7rem' }}
                    >
                      <span className={`text-${isDisabled ? 'danger' : 'success'}`}>
                        {data.registrationStatus}
                      </span>
                    </Badge>
                  </div>
                  {data.registeredDate && (
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Registered {new Date(data.registeredDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>

                {isAdmin && (
                  <Button
                    onClick={handleEditClick}
                    variant="primary"
                    size="sm"
                    className="d-flex align-items-center gap-1 flex-shrink-0 ms-sm-auto"
                    title="Edit client"
                  >
                    <Icon iconName={IconDefinitions.edit} />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </div>

          <ClientStatusCard
            isOnline={!!isOnline}
            lastSeenDate={data.lastSeenDate}
            companyName={data.company?.name ?? null}
            isHeartbeatLoggingOn={data.isHeartbeatLoggingOn}
            runningVersion={data.runningVersion}
          />

          <SystemInformationCard
            hostInfo={hostInfo}
            heartbeatDateTime={latestHeartbeat?.dateTime ?? null}
            isOnline={!!isOnline}
          />

          {canViewConfiguration && (
            <ActiveConfigurationCard activeConfiguration={activeConfiguration} isOnline={!!isOnline} />
          )}

          {/* Data Files Grid  */}
          <DataFilesGrid key={dataFilesKey} />

          {isAdmin && (
            <div className="card shadow-sm border-danger mb-4">
              <div className="card-header bg-danger bg-opacity-10 border-danger">
                <h5 className="mb-0 text-danger d-flex align-items-center" style={{ fontSize: 'clamp(0.9rem, 3vw, 1.25rem)' }}>
                  <Icon iconName={IconDefinitions.warning} className="me-2" />
                  Danger Zone
                </h5>
              </div>
              <div className="card-body p-3 p-sm-4">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
                  <div>
                    <h6 className="mb-1">
                      {isDisabled ? 'Enable Client' : 'Disable Client'}
                    </h6>
                    <p className="text-muted mb-0 small">
                      {isDisabled
                        ? 'This client is currently disabled and cannot send heartbeats or receive commands. Enable it to restore functionality.'
                        : 'Disabling this client will prevent it from sending commands and receiving heartbeats. This action can be reversed.'}
                    </p>
                  </div>
                  <Button
                    onClick={handleDisableClick}
                    variant={isDisabled ? 'success' : 'danger'}
                    className="flex-shrink-0 align-self-stretch align-self-sm-auto"
                  >
                    <Icon
                      iconName={isDisabled ? IconDefinitions.check : IconDefinitions.forbidden}
                      className="me-2"
                    />
                    {isDisabled ? 'Enable' : 'Disable'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {/* Modals */}
          <EditClientModal
            showState={showEditModalState}
            client={clientForModal}
            onSuccess={handleModalSuccess}
          />
          <DisableClientModal
            key={`${id}-${data?.registrationStatus}`}
            showState={showDisableModalState}
            client={clientForModal}
            onSuccess={handleModalSuccess}
          />
        </div>
      </div>
    </>
  );
}

export default CTEyeClientDetails;